/**
 * AuthService — API client for Identity SSO auth endpoints.
 * Handles login, signup, password reset, email verification.
 */

const IDENTITY_BASE = '/identity';

export interface LoginResponse {
  token: string;
  user: {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
    role: string;
    status: string;
    email_verified: boolean;
    accessible_apps: string[];
    app_roles: Record<string, string>;
    supporter: boolean;
    plan_key: string;
  };
  must_change_password?: boolean;
}

export interface SignupResponse {
  success: boolean;
  message: string;
  user_id?: number;
}

export interface AuthError {
  error: string;
  code?: string;
  details?: string;
  remaining_attempts?: number;
  locked_until?: string;
}

export type AuthResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: AuthError };

async function authFetch<T>(
  endpoint: string,
  body: Record<string, unknown>,
  bearerToken?: string,
): Promise<AuthResult<T>> {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (bearerToken) headers.Authorization = `Bearer ${bearerToken}`;
    const res = await fetch(`${IDENTITY_BASE}${endpoint}`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(body),
    });

    const data = await res.json();

    if (res.ok) {
      return { ok: true, data: data as T };
    }

    return {
      ok: false,
      error: {
        error: data.error || data.message || 'Unknown error',
        code: data.code,
        details: data.details,
        remaining_attempts: data.remaining_attempts,
        locked_until: data.locked_until,
      },
    };
  } catch {
    return {
      ok: false,
      error: { error: 'network_error', code: 'NETWORK_ERROR' },
    };
  }
}

/**
 * Login with email + password + optional Turnstile token.
 */
export async function login(
  email: string,
  password: string,
  turnstileToken?: string,
): Promise<AuthResult<LoginResponse>> {
  const body: Record<string, unknown> = { email, password };
  if (turnstileToken) body.cf_turnstile_token = turnstileToken;
  return authFetch<LoginResponse>('/login', body);
}

/**
 * Signup with minimal fields. last_name defaults to '-' if empty.
 */
export async function signup(
  email: string,
  password: string,
  firstName: string,
  lastName: string,
  turnstileToken?: string,
): Promise<AuthResult<SignupResponse>> {
  const body: Record<string, unknown> = {
    email,
    password,
    first_name: firstName,
    last_name: lastName || '-',
  };
  if (turnstileToken) body.cf_turnstile_token = turnstileToken;
  return authFetch<SignupResponse>('/signup', body);
}

/**
 * Request password reset email.
 */
export async function forgotPassword(
  email: string,
  turnstileToken?: string,
): Promise<AuthResult<{ message: string }>> {
  const body: Record<string, unknown> = { email };
  if (turnstileToken) body.cf_turnstile_token = turnstileToken;
  return authFetch<{ message: string }>('/forgot-password', body);
}

/**
 * Reset password with token from email.
 */
export async function resetPassword(
  token: string,
  password: string,
): Promise<AuthResult<{ message: string }>> {
  return authFetch<{ message: string }>('/reset-password', {
    token,
    password,
    password_confirmation: password,
  });
}

/**
 * Verify email with token.
 */
export async function verifyEmail(
  token: string,
): Promise<AuthResult<{ message: string }>> {
  try {
    const res = await fetch(
      `${IDENTITY_BASE}/verify-email?token=${encodeURIComponent(token)}`,
      { credentials: 'include' },
    );
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      return { ok: true, data: { message: data.message || 'Email verified' } };
    }
    return {
      ok: false,
      error: { error: data.error || 'Verification failed', code: data.code },
    };
  } catch {
    return { ok: false, error: { error: 'network_error', code: 'NETWORK_ERROR' } };
  }
}

/**
 * Resend verification email.
 */
export async function resendVerification(
  email: string,
): Promise<AuthResult<{ message: string }>> {
  return authFetch<{ message: string }>('/resend-verification', { email });
}

/**
 * Enter as a guest with just a nickname.
 * Backend creates a tier-0 user (is_guest=1, synthetic email).
 * Returns a JWT identical in shape to login() so the rest of the app needs no changes.
 */
export async function enterGuest(
  displayName: string,
  locale?: string,
): Promise<AuthResult<LoginResponse>> {
  const body: Record<string, unknown> = { display_name: displayName };
  if (locale) body.locale = locale;
  return authFetch<LoginResponse>('/enter', body);
}

/**
 * Upgrade a guest account to a full account by adding email + password.
 * Requires the guest JWT in the Authorization header.
 * Backend triggers a verification email.
 */
export async function claimAccount(
  guestToken: string,
  email: string,
  password: string,
  firstName?: string,
): Promise<AuthResult<LoginResponse>> {
  const body: Record<string, unknown> = { email, password };
  if (firstName) body.first_name = firstName;
  return authFetch<LoginResponse>('/claim', body, guestToken);
}
